// =============================================================================
// Organization Switcher Dialog
// Permet de changer, créer ou supprimer une organisation
// =============================================================================

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { deleteOrganization, createOrganizationViaEdgeFunction } from '@/modules/core/data/organizationRepository';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Building2,
  Check,
  Plus,
  Trash2,
  Loader2,
  Crown,
  Users,
} from 'lucide-react';
import type { Organization } from '@/types/database';

interface OrganizationSwitcherDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function OrganizationSwitcherDialog({
  open,
  onOpenChange,
}: OrganizationSwitcherDialogProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    organizations,
    currentOrganization,
    currentRole,
    setCurrentOrganization,
    refreshOrganizations,
  } = useOrganization();

  // Create organization state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [creating, setCreating] = useState(false);

  // Delete organization state
  const [orgToDelete, setOrgToDelete] = useState<Organization | null>(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleting, setDeleting] = useState(false);

  const isOwnerOrAdmin = currentRole === 'owner' || currentRole === 'admin';

  const handleSwitchOrganization = (org: Organization) => {
    if (org.id === currentOrganization?.id) return;
    
    setCurrentOrganization(org);
    toast.success(`Vous êtes maintenant sur ${org.name}`);
    onOpenChange(false);
    navigate('/dashboard', { replace: true });
  };

  const handleCreateOrganization = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !newOrgName.trim()) return;

    setCreating(true);

    try {
      const data = await createOrganizationViaEdgeFunction(newOrgName.trim());
      
      await refreshOrganizations();

      if (data?.organization) {
        setCurrentOrganization(data.organization as Organization);
        toast.success(`Workspace "${newOrgName.trim()}" créé avec succès`);
      }

      setNewOrgName('');
      setShowCreateForm(false);
      onOpenChange(false);
      navigate('/admin/access?tab=members');
    } catch (err: any) {
      console.error('Error creating organization:', err);
      toast.error(err.message || 'Échec de la création du workspace');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteOrganization = async () => {
    if (!orgToDelete || !user) return;

    setDeleting(true);

    try {
      await deleteOrganization(orgToDelete.id);

      // If deleting current org, switch to another or clear
      if (orgToDelete.id === currentOrganization?.id) {
        const remainingOrgs = organizations.filter(o => o.id !== orgToDelete.id);
        if (remainingOrgs.length > 0) {
          setCurrentOrganization(remainingOrgs[0]);
          navigate('/dashboard', { replace: true });
        } else {
          setCurrentOrganization(null);
          navigate('/onboarding');
        }
      }

      await refreshOrganizations();
      toast.success(`Workspace "${orgToDelete.name}" supprimé`);
      setOrgToDelete(null);
      setDeleteConfirmName('');
    } catch (err: any) {
      console.error('Error deleting organization:', err);
      toast.error(err.message || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  const getRoleBadge = (org: Organization) => {
    if (org.id === currentOrganization?.id && currentRole) {
      const roleLabels: Record<string, string> = {
        owner: 'Propriétaire',
        admin: 'Admin',
        member: 'Membre',
        guest: 'Invité',
      };
      return (
        <Badge variant={currentRole === 'owner' ? 'default' : 'secondary'} className="ml-2">
          {roleLabels[currentRole] || currentRole}
        </Badge>
      );
    }
    return null;
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Gérer les workspaces
            </DialogTitle>
            <DialogDescription>
              Changez de workspace, créez-en un nouveau ou supprimez un existant.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Organizations List */}
            <div>
              <Label className="text-sm font-medium text-muted-foreground mb-2 block">
                Vos workspaces ({organizations.length})
              </Label>
              <ScrollArea className="max-h-[200px]">
                <div className="space-y-2">
                  {organizations.map((org) => (
                    <div
                      key={org.id}
                      className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                        org.id === currentOrganization?.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:bg-muted/50'
                      }`}
                      onClick={() => handleSwitchOrganization(org)}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          {org.logo_url ? (
                            <img
                              src={org.logo_url}
                              alt={org.name}
                              className="w-8 h-8 rounded-lg object-cover"
                            />
                          ) : (
                            <Building2 className="h-4 w-4 text-primary" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center">
                            <span className="font-medium truncate">{org.name}</span>
                            {getRoleBadge(org)}
                          </div>
                          <span className="text-xs text-muted-foreground font-mono">
                            {org.slug}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {org.id === currentOrganization?.id && (
                          <Check className="h-4 w-4 text-primary" />
                        )}
                        {isOwnerOrAdmin && organizations.length > 1 && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOrgToDelete(org);
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>

            <Separator />

            {/* Create Organization Section */}
            {showCreateForm ? (
              <form onSubmit={handleCreateOrganization} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="org-name">Nom du nouveau workspace</Label>
                  <Input
                    id="org-name"
                    value={newOrgName}
                    onChange={(e) => setNewOrgName(e.target.value)}
                    placeholder="Mon nouveau workspace"
                    disabled={creating}
                    autoFocus
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setShowCreateForm(false);
                      setNewOrgName('');
                    }}
                    disabled={creating}
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1"
                    disabled={creating || !newOrgName.trim()}
                  >
                    {creating ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Création...
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        Créer
                      </>
                    )}
                  </Button>
                </div>
              </form>
            ) : (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setShowCreateForm(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Créer un nouveau workspace
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!orgToDelete} onOpenChange={() => setOrgToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">
              Supprimer le workspace
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                Vous êtes sur le point de supprimer le workspace{' '}
                <strong>"{orgToDelete?.name}"</strong>. Cette action est irréversible.
              </p>
              <p>
                Toutes les données associées (projets, tâches, membres, etc.) seront
                définitivement supprimées.
              </p>
              <div className="pt-2">
                <Label htmlFor="confirm-name" className="text-foreground">
                  Tapez <strong>{orgToDelete?.name}</strong> pour confirmer :
                </Label>
                <Input
                  id="confirm-name"
                  value={deleteConfirmName}
                  onChange={(e) => setDeleteConfirmName(e.target.value)}
                  placeholder={orgToDelete?.name}
                  className="mt-2"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setOrgToDelete(null);
                setDeleteConfirmName('');
              }}
            >
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteConfirmName !== orgToDelete?.name || deleting}
              onClick={(e) => {
                e.preventDefault();
                handleDeleteOrganization();
              }}
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Supprimer définitivement
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
